import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDebouncedValue } from "@mantine/hooks";
import HeroSectionV2 from "../../../components/HeroSectionV2";
import PersonsList from "./PersonsList";
import PersonModalForm from "./PersonModalForm";
import IDCardPrint from "./IDCardPrint";
import Billede1 from "../../../Images/Billede1.jpg";
import { Person, EventDto, Zone } from "./types";
import "./Persons.css";
import { getAllowedEventIds } from "../../../api/shiftsApi";

// -------------------- Types --------------------
interface FunctionWithZones {
    functionName: string;
    eventID: number;
    assignedZones?: Zone[];
}

interface Template {
    id: number;
    name: string;
    fields: any[];
    background?: string;
}

interface PagedResponse<T> {
    items: T[];
    page: number;
    pageSize: number;
    totalCount: number;
    totalPages: number;
}

type Filters = {
    search: string;
    eventName: string;
    functionName: string;
    country: string;
    dateFrom: string;
    dateTo: string;
    profileFilter: "all" | "with" | "without";
    printFilter: "all" | "printed" | "notPrinted";
};

const initialFilters: Filters = {
    search: "",
    eventName: "",
    functionName: "",
    country: "",
    dateFrom: "",
    dateTo: "",
    profileFilter: "all",
    printFilter: "all",
};

type PersonFormData = Person & {
    uploadImageBase64?: string;
};

// -------------------- Helpers --------------------
function normalizePersonFromApi(p: any): Person {
    return {
        ...p,
        events: p.events ?? [],
        functions: p.functions ?? [],
        zones: p.zones ?? [],
        hasProfileImage: p.hasImage ?? !!p.personImagePath,
        personImagePath: p.personImagePath ?? "",
        profileUrl: p.personImagePath ?? "",
    };
}

function getRoles(): string[] {
    const raw = sessionStorage.getItem("userRoles") || localStorage.getItem("userRoles");
    if (!raw) return [];

    try {
        const roles = JSON.parse(raw);
        return Array.isArray(roles) ? roles : [];
    } catch {
        return raw.split(",").map((r) => r.trim()).filter(Boolean);
    }
}

// -------------------- Component --------------------
const Persons: React.FC = () => {
    const apiUrl = process.env.REACT_APP_API_URL!;
    const currentUserId = Number(localStorage.getItem("userId") || 1);

    const roles = useMemo(() => getRoles(), []);
    const isAdmin = roles.includes("Admin");
    const isClub = roles.includes("Club") && !isAdmin;

    const [persons, setPersons] = useState<Person[]>([]);
    const [events, setEvents] = useState<EventDto[]>([]);
    const [countries, setCountries] = useState<string[]>([]);
    const [allFunctions, setAllFunctions] = useState<string[]>([]);
    const [functionsWithZones, setFunctionsWithZones] = useState<FunctionWithZones[]>([]);
    const [templates, setTemplates] = useState<Template[]>([]);

    const [filters, setFilters] = useState<Filters>(initialFilters);
    const [selectedPerson, setSelectedPerson] = useState<Person | null>(null);
    const [addingPerson, setAddingPerson] = useState(false);

    const [selectedPersonsForIDCard, setSelectedPersonsForIDCard] = useState<Person[]>([]);
    const [selectedTemplateId, setSelectedTemplateId] = useState<number | null>(null);
    const [showIDCardPreview, setShowIDCardPreview] = useState(false);
    const [printedIds, setPrintedIds] = useState<number[] | undefined>(undefined);

    const [page, setPage] = useState(1);
    const [pageSize] = useState(50);
    const [totalCount, setTotalCount] = useState(0);
    const [totalPages, setTotalPages] = useState(1);

    const [loading, setLoading] = useState(true);
    const [loadingPersons, setLoadingPersons] = useState(false);
    const [errorMsg, setErrorMsg] = useState<string>("");

    const [debouncedSearch] = useDebouncedValue(filters.search, 300);

    const editRequestRef = useRef(0);

    useEffect(() => {
        setPage(1);
    }, [
        debouncedSearch,
        filters.eventName,
        filters.functionName,
        filters.country,
        filters.dateFrom,
        filters.dateTo,
        filters.profileFilter,
        filters.printFilter,
    ]);

    useEffect(() => {
        const fetchBaseData = async () => {
            setLoading(true);
            setErrorMsg("");

            try {
                const [eventsRes, functionsRes, templatesRes, countriesRes] = await Promise.all([
                    fetch(`${apiUrl}/api/events`),
                    fetch(`${apiUrl}/api/function`),
                    fetch(`${apiUrl}/api/IDTemplate`),
                    fetch(`${apiUrl}/api/persons/countries`),
                ]);

                if (!eventsRes.ok || !functionsRes.ok || !templatesRes.ok || !countriesRes.ok) {
                    throw new Error("Kunne ikke hente data fra API");
                }

                const eventsData: EventDto[] = await eventsRes.json();
                let finalEvents = eventsData;

                if (isClub) {
                    const allowedIds = await getAllowedEventIds(apiUrl, currentUserId);
                    const allowedSet = new Set(allowedIds);
                    finalEvents = eventsData.filter((ev) => allowedSet.has(ev.eventID));
                }

                setEvents(finalEvents);

                if (isClub && finalEvents.length > 0) {
                    setFilters((f) => {
                        if (f.eventName) return f;
                        return { ...f, eventName: finalEvents[0].eventName, functionName: "" };
                    });
                } else {
                    setFilters((f) =>
                        f.eventName && !finalEvents.some((e) => e.eventName === f.eventName)
                            ? { ...f, eventName: "", functionName: "" }
                            : f
                    );
                }

                const functionsData: { functionId: number; functionName: string }[] = await functionsRes.json();
                setAllFunctions(functionsData.map((f) => f.functionName));

                const functionsZonesData: FunctionWithZones[] = await Promise.all(
                    functionsData.map(async (f) => {
                        const [zonesRes, funcRes] = await Promise.all([
                            fetch(`${apiUrl}/api/zones/functions/byfunction/${f.functionId}`),
                            fetch(`${apiUrl}/api/function/${f.functionId}`),
                        ]);

                        const zones: Zone[] = zonesRes.ok ? await zonesRes.json() : [];
                        const funcData = funcRes.ok ? await funcRes.json() : { eventID: null };

                        return {
                            functionName: f.functionName,
                            eventID: funcData.eventID,
                            assignedZones: zones,
                        };
                    })
                );

                setFunctionsWithZones(functionsZonesData);
                setTemplates(await templatesRes.json());
                setCountries(await countriesRes.json());
            } catch (err) {
                console.error(err);
                setErrorMsg("Der skete en fejl ved hentning af data.");
            } finally {
                setLoading(false);
            }
        };

        fetchBaseData();
    }, [apiUrl, currentUserId, isClub]);

    useEffect(() => {
        const fetchPersons = async () => {
            setLoadingPersons(true);
            setErrorMsg("");

            try {
                const params = new URLSearchParams();

                params.set("page", String(page));
                params.set("pageSize", String(pageSize));

                if (debouncedSearch.trim()) params.set("search", debouncedSearch.trim());
                if (filters.eventName) params.set("eventName", filters.eventName);
                if (filters.functionName) params.set("functionName", filters.functionName);
                if (filters.country) params.set("country", filters.country);
                if (filters.dateFrom) params.set("dateFrom", filters.dateFrom);
                if (filters.dateTo) params.set("dateTo", filters.dateTo);

                if (filters.profileFilter === "with") {
                    params.set("onlyWithProfile", "true");
                }

                if (filters.profileFilter === "without") {
                    params.set("onlyWithoutProfile", "true");
                }

                if (filters.printFilter === "printed") {
                    params.set("onlyPrinted", "true");
                }

                if (filters.printFilter === "notPrinted") {
                    params.set("onlyNotPrinted", "true");
                }

                const selectedEventForPrinted = events.find((e) => e.eventName === filters.eventName);
                if (
                    (filters.printFilter === "printed" || filters.printFilter === "notPrinted") &&
                    selectedEventForPrinted
                ) {
                    params.set("printedEventId", String(selectedEventForPrinted.eventID));
                }

                const res = await fetch(`${apiUrl}/api/persons?${params.toString()}`);

                if (!res.ok) {
                    throw new Error("Kunne ikke hente personer");
                }

                const data: PagedResponse<any> = await res.json();

                setPersons((data.items ?? []).map(normalizePersonFromApi));
                setTotalCount(data.totalCount ?? 0);
                setTotalPages(data.totalPages ?? 1);
            } catch (err) {
                console.error(err);
                setErrorMsg("Der skete en fejl ved hentning af personer.");
            } finally {
                setLoadingPersons(false);
            }
        };

        fetchPersons();
    }, [
        apiUrl,
        page,
        pageSize,
        debouncedSearch,
        filters.eventName,
        filters.functionName,
        filters.country,
        filters.dateFrom,
        filters.dateTo,
        filters.profileFilter,
        filters.printFilter,
        events,
    ]);

    useEffect(() => {
        fetch(`${apiUrl}/api/persons/printed`)
            .then((res) => res.json())
            .then((data: { personId: number }[]) => {
                setPrintedIds(data.map((d) => d.personId));
            })
            .catch((err) => console.error("Failed to load printed persons", err));
    }, [apiUrl]);

    useEffect(() => {
        setSelectedPersonsForIDCard([]);
    }, [filters, page]);

    const handleEdit = async (id: number) => {
        const requestId = ++editRequestRef.current;

        const cached = persons.find((p) => p.personId === id);
        if (cached) setSelectedPerson(cached);

        try {
            const res = await fetch(`${apiUrl}/api/persons/${id}`);
            if (!res.ok) return;

            const data = normalizePersonFromApi(await res.json());

            if (editRequestRef.current === requestId) {
                setSelectedPerson(data);
            }
        } catch (err) {
            console.error(err);
        }
    };

    const handleCloseEdit = () => {
        editRequestRef.current++;
        setSelectedPerson(null);
    };

    const handleDelete = async (id: number) => {
        if (!window.confirm("Er du sikker på, at du vil slette denne person?")) return;

        try {
            const res = await fetch(`${apiUrl}/api/persons/${id}`, { method: "DELETE" });

            if (!res.ok) {
                alert("Fejl ved sletning af personen");
                return;
            }

            const params = new URLSearchParams();
            params.set("page", String(page));
            params.set("pageSize", String(pageSize));

            if (filters.search.trim()) params.set("search", filters.search.trim());
            if (filters.eventName) params.set("eventName", filters.eventName);
            if (filters.functionName) params.set("functionName", filters.functionName);
            if (filters.country) params.set("country", filters.country);
            if (filters.dateFrom) params.set("dateFrom", filters.dateFrom);
            if (filters.dateTo) params.set("dateTo", filters.dateTo);

            if (filters.profileFilter === "with") {
                params.set("onlyWithProfile", "true");
            }

            if (filters.printFilter === "printed") {
                params.set("onlyPrinted", "true");
            }

            if (filters.printFilter === "notPrinted") {
                params.set("onlyNotPrinted", "true");
            }

            const selectedEventForPrinted = events.find((e) => e.eventName === filters.eventName);
            if (
                (filters.printFilter === "printed" || filters.printFilter === "notPrinted") &&
                selectedEventForPrinted
            ) {
                params.set("printedEventId", String(selectedEventForPrinted.eventID));
            }

            const reloadRes = await fetch(`${apiUrl}/api/persons?${params.toString()}`);
            if (reloadRes.ok) {
                const data: PagedResponse<any> = await reloadRes.json();
                setPersons((data.items ?? []).map(normalizePersonFromApi));
                setTotalCount(data.totalCount ?? 0);
                setTotalPages(data.totalPages ?? 1);
            }
        } catch (err) {
            console.error(err);
            alert("Fejl ved sletning af personen");
        }
    };

    const handleUpdate = async (updatedPerson: PersonFormData) => {
        const dto = {
            ...updatedPerson,
            events: updatedPerson.events ?? [],
            functions: updatedPerson.functions ?? [],
            uploadImageBase64: updatedPerson.uploadImageBase64 ?? "",
            personImagePath: updatedPerson.personImagePath ?? "",
        };

        try {
            const res = await fetch(`${apiUrl}/api/persons/${updatedPerson.personId}`, {
                method: "PUT",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(dto),
            });

            if (!res.ok) {
                console.error(await res.text());
                alert("Fejl ved opdatering af personen");
                return;
            }

            const updatedRaw = await res.json();
            const updated = normalizePersonFromApi({
                ...updatedRaw,
                hasImage: updatedRaw.hasImage ?? !!updatedRaw.personImagePath,
            });

            setPersons((prev) => prev.map((p) => (p.personId === updated.personId ? updated : p)));
            setSelectedPerson(null);
        } catch (err) {
            console.error(err);
            alert("Kunne ikke opdatere personen. Tjek API'en.");
        }
    };

    const handleCreatePerson = async (newPerson: PersonFormData) => {
        const dto = {
            ...newPerson,
            userId: newPerson.userId ?? currentUserId,
            events: newPerson.events ?? [],
            functions: newPerson.functions ?? [],
            uploadImageBase64: newPerson.uploadImageBase64 ?? "",
            personImagePath: newPerson.personImagePath ?? "",
        };

        try {
            const res = await fetch(`${apiUrl}/api/persons`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(dto),
            });

            if (!res.ok) {
                console.error(await res.text());
                alert("Fejl ved oprettelse af personen");
                return;
            }

            setAddingPerson(false);
            setPage(1);
        } catch (err) {
            console.error(err);
            alert("Kunne ikke oprette personen. Tjek API'en.");
        }
    };

    const selectedEvent = filters.eventName
        ? events.find((ev) => ev.eventName === filters.eventName) ?? null
        : null;

    const functionsForSelectedEvent = useMemo(() => {
        if (!selectedEvent) return [];

        return Array.from(
            new Set(
                functionsWithZones
                    .filter((fz) => fz.eventID === selectedEvent.eventID)
                    .map((fz) => fz.functionName)
            )
        ).sort();
    }, [functionsWithZones, selectedEvent]);

    const personsForPrint = useMemo(() => {
        if (!selectedEvent) return [];

        return selectedPersonsForIDCard.map((p) => {
            const functionsInEvent = (p.functions ?? []).filter((fnName) =>
                functionsWithZones.some(
                    (fz) => fz.functionName === fnName && fz.eventID === selectedEvent.eventID
                )
            );

            const zonesInEvent: Zone[] = [];

            functionsInEvent.forEach((fnName) => {
                const fz = functionsWithZones.find(
                    (f) => f.functionName === fnName && f.eventID === selectedEvent.eventID
                );
                if (fz?.assignedZones) zonesInEvent.push(...fz.assignedZones);
            });

            return { ...p, functions: functionsInEvent, zones: zonesInEvent };
        });
    }, [selectedEvent, selectedPersonsForIDCard, functionsWithZones]);

    const handlePrinted = async (printedPersons: Person[]) => {
        if (!selectedEvent) return;

        try {
            const payload = printedPersons.map((p) => ({
                personId: p.personId,
                eventId: selectedEvent.eventID,
            }));

            const res = await fetch(`${apiUrl}/api/persons/print-card`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(payload),
            });

            if (!res.ok) throw new Error("Kunne ikke logge print");

            setPrintedIds((prev = []) =>
                Array.from(new Set([...prev, ...printedPersons.map((p) => p.personId)]))
            );
        } catch (err) {
            console.error(err);
            alert("Fejl ved logging af print");
        }
    };

    return (
        <div>
            <HeroSectionV2
                titleEn="Persons"
                titleDa="Personer"
                descriptionEn="Manage all registered persons"
                descriptionDa="Her kan du se, redigere og slette personer"
                imageSrc={Billede1}
            />

            {errorMsg && <div style={{ padding: 20, color: "crimson" }}>{errorMsg}</div>}

            {!selectedPerson && (
                <div className="personsFiltersWrap">
                    <div className="personsFiltersCard">
                        <div className="personsFiltersGrid3">
                            <select
                                className="personsControl"
                                value={filters.eventName}
                                onChange={(e) =>
                                    setFilters((f) => ({
                                        ...f,
                                        eventName: e.target.value,
                                        functionName: "",
                                    }))
                                }
                            >
                                {!isClub && <option value="">Vælg event</option>}

                                {events.map((ev) => (
                                    <option key={ev.eventID} value={ev.eventName}>
                                        {ev.eventName}
                                    </option>
                                ))}
                            </select>

                            <select
                                className="personsControl"
                                value={filters.functionName}
                                disabled={!selectedEvent}
                                onChange={(e) =>
                                    setFilters((f) => ({ ...f, functionName: e.target.value }))
                                }
                            >
                                <option value="">
                                    {!selectedEvent ? "Vælg event først" : "Vælg funktion"}
                                </option>
                                {functionsForSelectedEvent.map((fn) => (
                                    <option key={fn} value={fn}>
                                        {fn}
                                    </option>
                                ))}
                            </select>

                            <input
                                className="personsControl personsSearchSpan2"
                                type="text"
                                placeholder="Søg efter navn..."
                                value={filters.search}
                                onChange={(e) =>
                                    setFilters((f) => ({ ...f, search: e.target.value }))
                                }
                            />
                        </div>

                        <details className="personsAdvanced">
                            <summary>Udvidede filtre</summary>

                            <div className="personsAdvancedGrid">
                                <div className="personsField">
                                    <label className="personsLabel">Land</label>
                                    <select
                                        className="personsControl"
                                        value={filters.country}
                                        onChange={(e) => setFilters((f) => ({ ...f, country: e.target.value }))}
                                    >
                                        <option value="">Alle lande</option>
                                        {countries.map((c) => (
                                            <option key={String(c)} value={String(c)}>
                                                {c}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                <div className="personsField">
                                    <label className="personsLabel">Profilbillede</label>
                                    <select
                                        className="personsControl"
                                        value={filters.profileFilter}
                                        onChange={(e) =>
                                            setFilters((f) => ({
                                                ...f,
                                                profileFilter: e.target.value as "all" | "with" | "without",
                                            }))
                                        }
                                    >
                                        <option value="all">Alle</option>
                                        <option value="with">Kun med profilbillede</option>
                                        <option value="without">Kun uden profilbillede</option>
                                    </select>
                                </div>

                                <div className="personsField">
                                    <label className="personsLabel">Printstatus</label>
                                    <select
                                        className="personsControl"
                                        value={filters.printFilter}
                                        onChange={(e) =>
                                            setFilters((f) => ({
                                                ...f,
                                                printFilter: e.target.value as "all" | "printed" | "notPrinted",
                                            }))
                                        }
                                    >
                                        <option value="all">Alle</option>
                                        <option value="printed">Kun printede</option>
                                        <option value="notPrinted">Kun ikke-printede</option>
                                    </select>
                                </div>

                                <div className="personsField">
                                    <label className="personsLabel">Oprettet fra</label>
                                    <input
                                        className="personsControl"
                                        type="date"
                                        value={filters.dateFrom}
                                        onChange={(e) => setFilters((f) => ({ ...f, dateFrom: e.target.value }))}
                                        aria-label="Oprettet fra"
                                    />
                                </div>

                                <div className="personsField">
                                    <label className="personsLabel">Oprettet til</label>
                                    <input
                                        className="personsControl"
                                        type="date"
                                        value={filters.dateTo}
                                        onChange={(e) => setFilters((f) => ({ ...f, dateTo: e.target.value }))}
                                        aria-label="Oprettet til"
                                    />
                                </div>
                            </div>
                        </details>

                        <div className="personsActionsRow">
                            <button className="btn btn--new" onClick={() => setAddingPerson(true)}>
                                Tilføj Person
                            </button>

                            <button
                                type="button"
                                className="btn btn--default"
                                onClick={() => setFilters(initialFilters)}
                            >
                                Clear Filters
                            </button>

                            {selectedPersonsForIDCard.length > 0 && (
                                <div className="personsActionsRight">
                                    <select
                                        className="personsControl personsTemplateSelect"
                                        value={selectedTemplateId ?? ""}
                                        onChange={(e) =>
                                            setSelectedTemplateId(
                                                e.target.value ? Number(e.target.value) : null
                                            )
                                        }
                                    >
                                        <option value="">Vælg skabelon</option>
                                        {templates.map((t) => (
                                            <option key={t.id} value={t.id}>
                                                {t.name}
                                            </option>
                                        ))}
                                    </select>

                                    <button
                                        className="btn btn-secondary"
                                        disabled={!selectedTemplateId || !selectedEvent}
                                        onClick={() => setShowIDCardPreview(true)}
                                    >
                                        Vis ID Kort
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {addingPerson && (
                <PersonModalForm
                    initialPerson={null}
                    onSave={handleCreatePerson}
                    onCancel={() => setAddingPerson(false)}
                    allEvents={events}
                    allFunctions={allFunctions}
                    currentUserId={currentUserId}
                />
            )}

            {selectedPerson && (
                <PersonModalForm
                    initialPerson={selectedPerson}
                    onSave={handleUpdate}
                    onCancel={handleCloseEdit}
                    allEvents={events}
                    allFunctions={allFunctions}
                    currentUserId={currentUserId}
                />
            )}

            {showIDCardPreview && selectedTemplateId && selectedEvent && (
                <IDCardPrint
                    persons={personsForPrint}
                    template={templates.find((t) => t.id === selectedTemplateId)!}
                    selectedEvent={selectedEvent}
                    functionsWithZones={functionsWithZones}
                    onClose={() => setShowIDCardPreview(false)}
                    onPrinted={handlePrinted}
                />
            )}

            {!selectedPerson && !addingPerson && printedIds && (
                <>
                    <PersonsList
                        persons={persons}
                        onEdit={handleEdit}
                        onDelete={handleDelete}
                        tableId="persons-table"
                        onSelectPersons={setSelectedPersonsForIDCard}
                        printedIds={printedIds}
                    />

                    <div
                        style={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            padding: "16px 20px",
                            gap: 12,
                            flexWrap: "wrap",
                        }}
                    >
                        <div>
                            Viser side {page} af {totalPages} · {totalCount} personer
                        </div>

                        <div style={{ display: "flex", gap: 8 }}>
                            <button
                                className="btn btn--default"
                                disabled={page <= 1}
                                onClick={() => setPage((p) => Math.max(1, p - 1))}
                            >
                                Forrige
                            </button>

                            <button
                                className="btn btn--default"
                                disabled={page >= totalPages}
                                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                            >
                                Næste
                            </button>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
};

export default Persons;